import React, {CSSProperties} from 'react';

type RequiredDotProps = {
  required?: boolean;
};

function RequiredDot({ required }: RequiredDotProps) {
  if (!required) return null;
  return <span className="required-dot" aria-label="required">*</span>;
}

type TextFieldProps = {
  [key: string]: any;
  label: React.ReactNode;
  required?: boolean;
  value: any;
  onChange: any;
  placeholder?: string;
  error?: React.ReactNode;
  testId?: string;
  errorTestId?: string;
  inputClassName?: string;
  wrapperClassName?: string;
};

export function TextField({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  error,
  testId,
  errorTestId,
  inputClassName = 'input',
  wrapperClassName = 'field',
  ...inputProps
}: TextFieldProps) {
  const finalInputClassName = `${inputClassName}${error ? ' error' : ''}`;
  const finalErrorTestId = errorTestId || (testId ? `${testId}-error` : undefined);

  return (
    <div className={wrapperClassName}>
      <label className="label">{label} <RequiredDot required={required} /></label>
      <input
        className={finalInputClassName}
        value={value ?? ''}
        onChange={onChange}
        aria-invalid={Boolean(error)}
        placeholder={placeholder}
        data-testid={testId}
        {...inputProps}
      />
      {error && <div className="error-text" data-testid={finalErrorTestId}>{error}</div>}
    </div>
  );
}

type SelectFieldProps = {
  [key: string]: any;
  label: React.ReactNode;
  required?: boolean;
  value: any;
  onChange: any;
  error?: React.ReactNode;
  testId?: string;
  errorTestId?: string;
  wrapperClassName?: string;
  className?: string;
  children?: React.ReactNode;
};



type TextAreaFieldProps = {
  [key: string]: any;
  label: React.ReactNode;
  value: any;
  onChange: any;
  placeholder?: string;
  rows?: number;
  wrapperClassName?: string;
  className?: string;
  testId?: string;
};

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  wrapperClassName = 'field',
  className = 'input',
  testId,
  ...textareaProps
}: TextAreaFieldProps) {
  return (
    <div className={wrapperClassName}>
      <label className="label">{label}</label>
      <textarea
        className={className}
        rows={rows}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        {...textareaProps}
      />
    </div>
  );
}

type FileFieldProps = {
  label: React.ReactNode;
  accept?: string;
  onChange: any;
  selectedName?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  testId?: string;
  multiple?: boolean;
  props?: any;
  disabled?: boolean;
  helpText?: string;
  removeAction?: () => void;
};

export function FileField({
  label,
  accept,
  onChange,
  selectedName,
  removeAction = null,
  wrapperClassName = 'field',
  inputClassName = 'input',
  testId,
  multiple = false,
  props,
  disabled = false,
  helpText
}: FileFieldProps) {
  return (
    <div className={wrapperClassName}>
      <label className="label">{label}</label>
      <input
        type="file"
        disabled={disabled}
        accept={accept}
        className={inputClassName}
        onChange={onChange}
        data-testid={testId}
        multiple={multiple}
        {...props}
      />
      <div>
        <small>{helpText}</small>
      </div>
      {selectedName && (
        <div style={{ fontSize: 12, color: '#5f6a6f', marginTop: 4 }}>
          Selected: {selectedName}
          {removeAction && <button type="button" className='button-outline' onClick={removeAction} title='Remove'>X</button>}
        </div>
      )}
    </div>
  );
}

export function SelectField({
  label,
  required = false,
  value,
  onChange,
  error,
  testId,
  errorTestId,
  wrapperClassName = 'field',
  className = 'select',
  children,
  ...selectProps
}: SelectFieldProps) {
  const finalClassName = `${className} ${error ? 'error' : ''}`;
  const finalErrorTestId = errorTestId || (testId ? `${testId}-error` : undefined);

  return (
    <div className={wrapperClassName}>
      <label className="label">{label} <RequiredDot required={required} /></label>
      <select
        className={finalClassName}
        value={value ?? ''}
        onChange={onChange}
        aria-invalid={Boolean(error)}
        data-testid={testId}
        {...selectProps}
      >
        {children}
      </select>
      {error && <div className="error-text" data-testid={finalErrorTestId}>{error}</div>}
    </div>
  );
}

export function DividerRow() {
  return (
    <div className="field full-span" style={{ marginBottom: 0 }}>
      <div className="divider" />
    </div>
  );
}

type ImmutableFieldLabelProps = {
  label: string;
  value: string | undefined;
  placeholder?: string;
  testId?: string;
  style?: CSSProperties;
}

export function ImmutableFieldLabel({ label, value, placeholder, testId, style }: ImmutableFieldLabelProps) {
  return (
    <div className='field'>
      <label className='label'>{label} </label>
      <label className='input' style={{fontSize: 15, ...style}} data-testid={testId}>{value || placeholder}</label>
    </div>
  );
}
